import type { PaletteModel, CameraPose } from '../data/types';
import type { StyleParams, SkyStop, PainterProjectionParams } from './types';
import type { LinearRGB } from '../core/color';
import { hexToLinear } from '../core/color';
import { derivePainterProjectionParams, STYLE_CONSTANTS } from './painterProjection';
import { painterFrameFromView } from './painterWarp';
import { committedResidualWarp } from './painterWarpData';
import { committedPainterStrokes } from './painterStrokesData';
import {
  CLEAR_SKY_STOPS_HEX,
  CLOUD_COVERAGE,
  CLOUD_HEIGHT_SHARE,
  CLOUD_MEDIAN_HEX,
  clearSkyStops,
} from './skyModel';

/** STYLE §5.1: the ink, the darkest 5% of the ridge pixels, mixed in linear RGB at 0.866. */
const LINEWORK_INK_HEX = '#0f0500';

/**
 * STYLE §5.1 target 10: the painter lays thin light strokes beside the lines he inks. 98% of the
 * painting's light strokes (5 x 5 white top-hat above 20 L*, the class `highlightWhite`) lie within
 * 2 px of a dark line; they cover 6.0% of the city inside its walls at the 2000 px frame, and the
 * render without them 5.0%. At 0.6 of each pixel on the painter view's 1000 px canvas, where the
 * line is 0.83 px wide, the render covers 6.0% (round 11). A face turned within 60 deg of the sun
 * takes them; the flat ground (cosine 0.40) does not.
 *
 * Round 18: a stroke takes half its colour from the face it lies on, that face's hue at the
 * highlight colour's luminance (`finalPass.ts lightStrokeColour`). The class median was measured on
 * the 2000 px copy, which blurs each stroke into its face, and drawn in it alone the strokes warmed
 * the slate roofs toward the tiles' hue; the share was fitted on the painter view (STYLE §5.1).
 */
const LIGHT_STROKE = { opacity: 0.72, litCos: 0.5, faceHue: 0.5 } as const;

/**
 * Creates StyleParams from STYLE.md measurements. Most palette colours reach the shaders through
 * the material factory; the light strokes of the final pass take the painting's highlight colour
 * here.
 */
export function createStyleParams(palette: PaletteModel, painterView: CameraPose): StyleParams {
  const hazeColor: LinearRGB = hexToLinear(CLEAR_SKY_STOPS_HEX[0]![1]);
  const skyStops: readonly SkyStop[] = clearSkyStops();
  const cloudColor: LinearRGB = hexToLinear(CLOUD_MEDIAN_HEX);

  const sunDirection: readonly [number, number, number] = [0.7621, 0.3953, 0.5128];

  const lineworkColor: LinearRGB = hexToLinear(LINEWORK_INK_HEX);

  const baseParams = {
    atmosphere: {
      hazeColor,
      density: 6.618e-5,
      exponent: 2,
      heightFalloff: 0.0,
      maxOpacity: 1.0,
      /* STYLE §3.2: the painting hazes its far fields (3.99-4.21 km in the painter camera) and not
         the fields nearer, which exp² hazes enough to turn polderFields into farFields from 2.9 km;
         the painter view's haze rises through the same calibration point as the 8th power. */
      calibrationDistanceM: 4210,
      painterExponent: 8,
    },
    sky: {
      stops: skyStops,
      cloudColor,
      cloudCoverage: CLOUD_COVERAGE,
      cloudScale: CLOUD_HEIGHT_SHARE,
      cloudDrift: 0,
    },
    light: {
      sunDirection,
      wrap: 0.456,
      bandCount: 0,
      bandSoftness: 0,
    },
    linework: {
      color: lineworkColor,
      widthPxAt1080: 0.9,
      opacity: 0.866,
      wobblePx: 0.39,
      depthThreshold: 0.08,
      normalThreshold: 0.7,
      /* STYLE §5.2: the painting outlines a house, roof or ship where it is broad enough to carry
         the stroke and leaves the smallest as dabs of colour. Outlining every object inked the
         city classes 1.2x as densely as the painting (mean |log2| 0.38 over 12 classes); objects
         that reach 6 px behind the outline on the 1000 px painter canvas match it (0.23). */
      outlineSpanLineWidths: 7,
      atmosphereFade: 0,
      highlight: { color: palette.highlightWhite.median.linear, ...LIGHT_STROKE },
    },
    brush: {
      strength: 0.165,
      strokeLengthM: 5.7,
      strokeWidthM: 1.9,
    },
    canvas: {
      grainStrength: 0.013,
      craquelureStrength: 0,
      craquelureCellPx: 2.2,
      vignetteStrength: 0,
      vignetteRadius: 1.0,
    },
  };

  return { ...baseParams, painterProjection: createPainterProjectionParams(painterView) };
}

/**
 * Creates painter projection parameters from the camera pose and STYLE constants.
 *
 * All values are derived mathematically from the camera pose:
 * - d0: forward distance where compression begins (92% of dH)
 * - dH: forward horizon distance (derived from painted horizon row)
 * - L: compression length scale (dH - d0)
 * - forwardDir/rightDir: camera axes for forward-axis compression
 * - verticalExaggeration: 3.54x - Schreierstoren painted/predicted ratio (STYLE.md §2)
 */
function createPainterProjectionParams(painterView: CameraPose): PainterProjectionParams {
  const derived = derivePainterProjectionParams(painterView, STYLE_CONSTANTS);

  const pitchRad = Math.atan2(
    painterView.target[1] - painterView.position[1],
    Math.sqrt(
      Math.pow(painterView.target[0] - painterView.position[0], 2) +
        Math.pow(painterView.target[2] - painterView.position[2], 2)
    )
  );
  const fovYRad = (painterView.fovYDeg * Math.PI) / 180;
  const horizonElevationTop = pitchRad + fovYRad / 2;

  return {
    frame: painterFrameFromView(painterView, STYLE_CONSTANTS),
    residualWarp: committedResidualWarp(),
    strokes: committedPainterStrokes(),
    groundPoint: derived.groundPoint,
    cameraHeight: derived.cameraHeight,
    d0: derived.d0,
    dH: derived.dH,
    L: derived.L,
    farKneeM: derived.farKneeM,
    farKneeShare: derived.farKneeShare,
    farScaleM: derived.farScaleM,
    farShear: derived.farShear,
    verticalExaggeration: derived.verticalExaggeration,
    horizonDepressionRad: derived.horizonDepressionRad,
    paintedHorizonFraction: STYLE_CONSTANTS.paintedHorizonRow / STYLE_CONSTANTS.imageHeight,
    horizonElevationTop: horizonElevationTop,
    forwardDir: derived.forwardDir,
    rightDir: derived.rightDir,
  };
}
